import { type LucideIcon } from "lucide-react";

import { ctaButtonBaseClassName, lightGradient } from "./ctaButtonStyles";

export interface ProcessCopy {
  header: {
    h2: string;
    h3: string;
    p: string;
  };
  stepNumber: string;
  steps: {
    Icon: LucideIcon;
    title: string;
    duration: string;
    description: string;
  }[];
  cta: {
    href: string;
    text: string;
  };
}

export function Process({ copy }: { copy: ProcessCopy }) {
  return (
    <section
      id="proces"
      className="py-24 bg-background text-white overflow-hidden"
    >
      <div className="container mx-auto px-6 md:px-12 max-w-5xl">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-sm font-semibold bg-clip-text text-transparent uppercase tracking-widest mb-3" style={{ backgroundImage: lightGradient }}>
            {copy.header.h2}
          </h2>
          <h3 className="text-3xl md:text-4xl font-semibold mb-6">
            {copy.header.h3}
          </h3>
          <p className="text-lg text-gray-400">{copy.header.p}</p>
        </div>

        <div className="relative">
          <div className="absolute left-8 md:left-1/2 top-10 bottom-0 w-px bg-slate-100 transform md:-translate-x-1/2 shadow-[0_0_10px_rgba(255,255,255,0.4),0_0_20px_rgba(255,255,255,0.15)]" />

          <div className="space-y-0 pb-12">
            {copy.steps.map((step, index) => {
              const isLeft = index % 2 === 0;
              return (
                <div
                  key={step.title}
                  className={`relative flex flex-col md:flex-row md:items-start w-full ${index > 0 ? "mt-8 md:-mt-10" : ""}`}
                >
                  {isLeft ? (
                    <>
                      <div className="w-full md:w-1/2 pl-24 md:pl-0 md:pr-12 lg:pr-16 mt-6">
                        <div className="bg-surface-dark rounded-2xl p-6 border border-gray-700 hover:border-[#d2fd4e]/80 hover:shadow-[0_0_8px_rgba(105,245,156,0.65),0_0_8px_rgba(214,253,75,0.35)]  duration-300 transition-shadows relative text-left md:text-right">
                          <div className="text-gray-400 font-semibold mb-3 flex items-center gap-3 justify-start md:justify-end">
                            <span className="hidden md:inline text-sm font-medium">
                              {step.duration}
                            </span>
                            <span className="text-gray-700 py-1 px-3 rounded-full text-xs uppercase tracking-wider" style={{ backgroundImage: lightGradient }}>
                              {copy.stepNumber} {index + 1}
                            </span>
                            <span className="inline md:hidden text-sm font-medium">
                              {step.duration}
                            </span>
                          </div>
                          <h4 className="text-xl font-semibold mb-3">
                            {step.title}
                          </h4>
                          <p className="text-slate-400 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                      <div className="hidden md:block md:w-1/2" />
                    </>
                  ) : (
                    <>
                      <div className="hidden md:block md:w-1/2" />
                      <div className="w-full md:w-1/2 pl-24 md:pl-12 lg:pl-16 mt-6">
                        <div className="bg-surface-dark rounded-2xl p-6 border border-gray-700 hover:border-[#d2fd4e]/80 hover:shadow-[0_0_8px_rgba(105,245,156,0.65),0_0_8px_rgba(214,253,75,0.35)]  duration-300 transition-shadows relative text-left">
                          <div className="text-gray-400 font-semibold mb-3 flex items-center gap-3 justify-start">
                            <span className="text-gray-700 py-1 px-3 rounded-full text-xs uppercase tracking-wider" style={{ backgroundImage: lightGradient }}>
                              {copy.stepNumber} {index + 1}
                            </span>
                            <span className="text-sm font-medium">
                              {step.duration}
                            </span>
                          </div>
                          <h4 className="text-xl font-semibold mb-3">
                            {step.title}
                          </h4>
                          <p className="text-slate-400 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="absolute left-8 md:left-1/2 transform -translate-x-1/2 top-6 md:top-8 w-12 h-12 md:w-16 md:h-16 bg-gray-800 border-[1px] border-gray-200 rounded-full flex items-center justify-center text-gray-200 z-10">
                    <div className="scale-75 md:scale-100">
                      <step.Icon className="w-5 h-5" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-12 text-center relative z-10">
          <a
            href={copy.cta.href}
            style={{ backgroundImage: lightGradient }}
            className={`${ctaButtonBaseClassName} inline-flex justify-center items-center gap-2 text-gray-700 font-semibold py-4 px-8 rounded-full text-lg`}
          >
            {copy.cta.text}
          </a>
        </div>
      </div>
    </section>
  );
}
